import FetchPreviewUrlRequest from '../types/FetchPreviewUrlRequest';
import { assertNever } from 'owa-assert';
import { ClientAttachmentId } from 'owa-client-ids';
import { getDownloadToken } from 'owa-files-url';
import * as OwaFilesUrl from 'owa-files-url';
import AttachmentUrlType from 'owa-files-url/lib/schema/AttachmentUrlType';
import { getOwaCanaryCookie } from 'owa-service/lib/canary';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import ReferenceAttachment from 'owa-service/lib/contract/ReferenceAttachment';

// Get the Url for cloudy attachments.
export function getAttachmentUrlForCloudy(
    attachment: AttachmentType,
    attachmentUrlType: AttachmentUrlType,
    isReadOnly: boolean,
    includeCanary: boolean = true
) {
    const {
        AttachmentId: attachmentId,
        Name: name,
        OriginalPermissionType: originalPermissionType,
        PermissionType: permissionType,
        AttachLongPathName: attachLongPathName,
    } = <ReferenceAttachment>attachment;

    switch (attachmentUrlType) {
        case AttachmentUrlType.FullFile:
        case AttachmentUrlType.Print:
            return OwaFilesUrl.getAttachmentUrlForCloudy(
                attachmentId,
                attachLongPathName,
                attachmentUrlType,
                isReadOnly,
                permissionType,
                originalPermissionType,
                includeCanary,
                name
            );
        case AttachmentUrlType.Preview:
            if ((<ReferenceAttachment>attachment).AttachmentPreviewUrl) {
                return (<ReferenceAttachment>attachment).AttachmentPreviewUrl;
            } else {
                return OwaFilesUrl.getAttachmentUrlForCloudy(
                    attachmentId,
                    attachLongPathName,
                    AttachmentUrlType.Preview,
                    isReadOnly,
                    permissionType,
                    originalPermissionType,
                    includeCanary,
                    name
                );
            }
        case AttachmentUrlType.Thumbnail:
            return (<ReferenceAttachment>attachment).AttachmentThumbnailUrl;
        default:
            return assertNever(attachmentUrlType);
    }
}

// TODO VSO#17696: Rename it to getAttachmentUrl
// Get the download Url for classic attachments.
export function getAttachmentUrlForClassic(
    attachmentId: ClientAttachmentId,
    attachmentUrlType: AttachmentUrlType,
    addIsDownloadQueryParam: boolean = false,
    includeDownloadToken: boolean = true
) {
    return OwaFilesUrl.getAttachmentUrlForClassic(
        attachmentId.mailboxInfo && attachmentId.mailboxInfo.mailboxId,
        attachmentId,
        attachmentUrlType,
        addIsDownloadQueryParam,
        includeDownloadToken
    );
}

/*
 * Initialize the preview URL
 * @returns {FetchPreviewUrl} The request to fetch the preview url
 */
export function getPreviewUrlRequest(
    attachmentId: ClientAttachmentId,
    attachment: AttachmentType,
    isCloudy: boolean,
    isReadyOnly: boolean,
    addIsDownloadQueryParam: boolean = false
): FetchPreviewUrlRequest {
    const previewUrl = isCloudy
        ? getAttachmentUrlForCloudy(
              attachment,
              AttachmentUrlType.Preview,
              isReadyOnly,
              false /* includeCanary */
          )
        : getAttachmentUrlForClassic(
              attachmentId,
              AttachmentUrlType.Preview,
              addIsDownloadQueryParam,
              false /* includeDownloadToken */
          );

    if (isCloudy) {
        return {
            url: previewUrl,
            init: {
                credentials: 'include',
            },
        };
    } else {
        // If the attachment is classic then we send the download token
        // as header
        const token = getDownloadToken();
        const headers = new Headers();
        headers.set('X-Token', token);
        headers.set('X-OWA-CANARY', getOwaCanaryCookie());
        return {
            url: previewUrl,
            init: {
                headers: headers,
            },
        };
    }
}

export default function getAttachmentUrl(
    attachmentId: ClientAttachmentId,
    attachment: AttachmentType,
    attachmentUrlType: AttachmentUrlType,
    isCloudy: boolean,
    isReadyOnly: boolean,
    addIsDownloadQueryParam: boolean = false
): string {
    const downloadUrl = isCloudy
        ? getAttachmentUrlForCloudy(attachment, attachmentUrlType, isReadyOnly)
        : getAttachmentUrlForClassic(attachmentId, attachmentUrlType, addIsDownloadQueryParam);
    return downloadUrl;
}
